import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Checkbox,
  Container,
  Stack,
  useMediaQuery,
  useTheme,
  IconButton,
  InputAdornment,
  Pagination,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PrintIcon from "@mui/icons-material/Print";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { markICUChargePaid, createICUCharge } from "../../../../api/icu";
import { listPatientEquipmentAssignments } from "../../../../api/medicalEquipment";
import { getObjects, addObject } from "../../../../api/Api";
import { getReceiptHeaderInfo } from "../../../../utils/hospitalInfo";

export default function CollectICUFees() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [selectedChargeIds, setSelectedChargeIds] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [printMeta, setPrintMeta] = useState({
    patientName: "",
    admissionId: null,
  });

  // Get all ICU charges (paid and unpaid)
  const allChargesQuery = useQuery({
    queryKey: ["icu-all-charges"],
    queryFn: () => getObjects("icu-charges", token),
  });

  // Get equipment charges as well (machines, oxygen, incubators, etc.)
  const equipmentChargesQuery = useQuery({
    queryKey: ["equipment-charges-icu"],
    queryFn: () => getObjects("equipment-charges", token),
  });

  const allCharges = useMemo(() => {
    const icu = Array.isArray(allChargesQuery.data)
      ? allChargesQuery.data
      : allChargesQuery.data?.results || [];
    const eq = Array.isArray(equipmentChargesQuery.data)
      ? equipmentChargesQuery.data
      : equipmentChargesQuery.data?.results || [];

    // Filter equipment charges to only ICU admissions
    // Normalize equipment charges to common shape
    const normalizedEq = eq
      .filter((c) => c.icu_admission || c.icu_admission_id)
      .map((c) => ({
        id: c.id,
        admission: c.icu_admission || c.icu_admission_id,
        admission_id: c.icu_admission_id || c.icu_admission,
        patient_name: c.patient_name,
        item_name: c.item_name || c.equipment_name || "Equipment",
        quantity: c.quantity || 1,
        unit_price: c.unit_price || c.rate || 0,
        total_amount: c.total_amount,
        is_paid: c.is_paid,
        charge_date: c.start_time || c.created_at || null,
        _source: "equipment",
      }));

    const normalizedIcu = icu.map((c) => ({ ...c, _source: "icu" }));
    return [...normalizedIcu, ...normalizedEq];
  }, [allChargesQuery.data, equipmentChargesQuery.data]);

  const toggle = (id) => {
    setSelectedChargeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const clear = () => setSelectedChargeIds([]);

  const markPaidMutation = useMutation({
    mutationFn: async (ids) => {
      for (const cid of ids) {
        const ch = allCharges.find((c) => c.id === cid);
        if (ch && ch._source === "equipment") {
          // eslint-disable-next-line no-await-in-loop
          await addObject(`equipment-charges/${cid}/mark_paid`, {}, token);
        } else {
          // eslint-disable-next-line no-await-in-loop
          await markICUChargePaid(cid, token);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["icu-all-charges"]);
      queryClient.invalidateQueries(["equipment-charges-icu"]);
      clear();
      // auto print receipt after success
      handlePrintReceipt();
    },
  });

  // Filters
  const filteredCharges = useMemo(() => {
    return allCharges.filter((c) => {
      const matchesSearch =
        !searchTerm ||
        (c.patient_name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (c.item_name || "").toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDate =
        !dateFilter || (c.charge_date || "").slice(0, 10) === dateFilter;
      return matchesSearch && matchesDate;
    });
  }, [allCharges, searchTerm, dateFilter]);

  const selectedCharges = useMemo(
    () => filteredCharges.filter((c) => selectedChargeIds.includes(c.id)),
    [filteredCharges, selectedChargeIds]
  );

  // Groups by admission for card view
  const groups = useMemo(() => {
    const map = new Map();
    for (const ch of filteredCharges) {
      const key = ch.admission_id || ch.admission;
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, {
          admissionId: key,
          patientName: ch.patient_name,
          charges: [],
        });
      }
      map.get(key).charges.push(ch);
    }
    return Array.from(map.values()).map((g) => {
      const total = g.charges.reduce(
        (s, c) => s + Number(c.total_amount || 0),
        0
      );
      const unpaid = g.charges.filter((c) => !c.is_paid);
      const isPaid = unpaid.length === 0;
      return { ...g, total, unpaid, isPaid };
    });
  }, [filteredCharges]);

  // Pagination for group cards
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const totalPages = Math.max(1, Math.ceil(groups.length / rowsPerPage));
  const pagedGroups = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return groups.slice(start, start + rowsPerPage);
  }, [groups, page, rowsPerPage]);

  // Equipment Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerAdmissionId, setDrawerAdmissionId] = useState(null);
  const [selectedEqIds, setSelectedEqIds] = useState([]);
  const openEquipmentDrawer = (admissionId) => {
    setDrawerAdmissionId(admissionId);
    setSelectedEqIds([]);
    setDrawerOpen(true);
  };
  const closeEquipmentDrawer = () => setDrawerOpen(false);

  const assignmentsQuery = useQuery({
    queryKey: ["equipment-assignments-by-icu-admission", drawerAdmissionId],
    queryFn: () => {
      // Use icu_admission parameter for ICU admissions
      return listPatientEquipmentAssignments(
        { icu_admission: drawerAdmissionId },
        token
      );
    },
    enabled: !!drawerAdmissionId && drawerOpen,
  });

  const assignments = useMemo(() => {
    const data = assignmentsQuery.data;
    return Array.isArray(data) ? data : data?.results || [];
  }, [assignmentsQuery.data]);

  const toggleEq = (id) => {
    setSelectedEqIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const totalSelected = useMemo(
    () =>
      selectedCharges.reduce((sum, c) => sum + Number(c.total_amount || 0), 0),
    [selectedCharges]
  );

  const handlePrintReceipt = (meta) => {
    if (selectedCharges.length === 0) return;
    setPrinting(true);
    const { hospitalName, hospitalAddress, hospitalPhone } =
      getReceiptHeaderInfo(t);
    const admissionId = meta?.admissionId || printMeta.admissionId || "-";
    const patientName =
      meta?.patientName ||
      printMeta.patientName ||
      selectedCharges[0]?.patient_name ||
      "-";
    const w = window.open("", "_blank");
    if (!w) {
      setPrinting(false);
      return;
    }
    const rows = selectedCharges
      .map(
        (c, i) =>
          `<tr><td>${i + 1}</td><td>${c.patient_name || ""}</td><td>${
            c.item_name
          }</td><td style="text-align:right">${
            c.quantity
          }</td><td style="text-align:right">${
            c.unit_price
          } AFN</td><td style="text-align:right">${
            c.total_amount
          } AFN</td></tr>`
      )
      .join("");
    w.document.write(`
      <html><head><title>Receipt</title>
      <style>
        body{font-family: Arial; padding:16px; color:#000}
        .header{border-bottom:2px solid #000; padding-bottom:8px; margin-bottom:12px}
        .title{font-size:18px; font-weight:bold}
        .meta{font-size:12px}
        .footer{border-top:1px dashed #000; margin-top:12px; padding-top:8px; font-size:12px; text-align:center}
        table{width:100%; border-collapse:collapse; margin-top:12px}
        td,th{border:1px solid #ddd; padding:6px; font-size:12px}
        .right{text-align:right}
      </style></head><body>
        <div class="header">
          <div class="title">${hospitalName}</div>
          <div class="meta">${hospitalAddress} | ${hospitalPhone}</div>
        </div>
        <div class="meta">${t("print.type", { defaultValue: "Receipt Type" })}: ICU</div>
        <div class="meta">${t("reception.admission", { defaultValue: "Admission" })}: #${admissionId}</div>
        <div class="meta">${t("print.patient", { defaultValue: "Patient" })}: ${patientName}</div>
        <div class="meta">${t("print.date", { defaultValue: "Date" })}: ${new Date().toLocaleString()}</div>
        <table><thead><tr><th>#</th><th>${t("print.patient", { defaultValue: "Patient" })}</th><th>${t("print.item", { defaultValue: "Item" })}</th><th>${t("print.quantity", { defaultValue: "Qty" })}</th><th>${t("print.unit", { defaultValue: "Unit" })}</th><th>${t("print.total", { defaultValue: "Total" })}</th></tr></thead>
        <tbody>${rows}</tbody></table>
        <h3 class="right">${t("print.totalAmount", { defaultValue: "Grand Total" })}: ${totalSelected.toFixed(2)} AFN</h3>
        <div class="footer">
          <div>${t("print.thanks", { defaultValue: "Thank you for choosing" })}</div>
          <div class="bold">${hospitalName}</div>
          <div>${t("print.cosmos", { defaultValue: "Powered by Afghan Cosmos IT and Solution" })}</div>
          <div>Receipt generated: ${new Date().toLocaleString()}</div>
        </div>
        <script>window.print(); setTimeout(()=>window.close(), 300);</script>
      </body></html>
    `);
    w.document.close();
    setPrinting(false);
  };

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 5,
          mb: 4,
          gap: 2,
          p: 2,
          bgcolor: "#f4f6fa",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          color="primary"
          sx={{ mb: isSmallScreen ? 2 : 0 }}
        >
          {t("reception.icuFees") || "Collect ICU Fees"}
        </Typography>
        <Stack
          direction={isSmallScreen ? "column" : "row"}
          gap={2}
          sx={{ width: isSmallScreen ? "100%" : "auto" }}
        >
          <TextField
            variant="outlined"
            size="small"
            fullWidth={isSmallScreen}
            value={searchTerm}
            placeholder={t("reception.search", "Search patient, item...")}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              sx: { borderRadius: 2, bgcolor: "#fff", boxShadow: 1 },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm("")} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 200,
              maxWidth: 320,
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: 1,
            }}
          />
          <TextField
            type="date"
            variant="outlined"
            size="small"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: dateFilter && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setDateFilter("")} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 180,
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: 1,
            }}
            label={t("reception.filterByDate", "Filter by Date")}
          />
        </Stack>
      </Box>

      <Grid container spacing={3} padding="20px" mt={3}>
        {allChargesQuery.isLoading ? (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : pagedGroups.length === 0 ? (
          <Grid item xs={12}>
            <Box textAlign="center" mt={4}>
              <Typography color="text.secondary">
                {t("reception.noMatching", "No matching records.")}
              </Typography>
            </Box>
          </Grid>
        ) : (
          pagedGroups.map((group) => (
            <Grid item xs={12} md={6} key={group.admissionId}>
              <Paper
                elevation={group.isPaid ? 2 : 4}
                sx={{
                  mb: 3,
                  p: 3,
                  borderRadius: 3,
                  width: "100%",
                  background: group.isPaid
                    ? "linear-gradient(90deg, #f5f5f5 70%, #f4f6fa 100%)"
                    : "linear-gradient(90deg, #ffebee 60%, #fffde7 100%)",
                  borderLeft: group.isPaid
                    ? "4px solid #009688"
                    : "4px solid #d32f2f",
                  transition: "box-shadow 0.2s",
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  mb={2}
                >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="primary"
                    sx={{ fontSize: isSmallScreen ? "1rem" : "1.1rem" }}
                  >
                    {t("reception.admission", "Admission")} #{group.admissionId}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={
                        group.isPaid
                          ? t("reception.paid", "Paid")
                          : t("reception.pending", "Pending")
                      }
                      color={group.isPaid ? "success" : "warning"}
                      sx={{
                        fontWeight: "bold",
                        fontSize: isSmallScreen ? "0.8rem" : "0.95rem",
                        px: 1,
                      }}
                    />
                    <IconButton
                      color="primary"
                      onClick={() => {
                        setSelectedChargeIds(group.charges.map((c) => c.id));
                        setTimeout(() => handlePrintReceipt(), 100);
                      }}
                    >
                      <PrintIcon />
                    </IconButton>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openEquipmentDrawer(group.admissionId)}
                    >
                      {t("reception.equipmentDetails", "Equipment Details")}
                    </Button>
                  </Box>
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ mt: 1, mb: 1 }}
                >
                  <Typography fontSize={isSmallScreen ? "0.92rem" : "1rem"}>
                    <strong>{t("reception.patient", "Patient")}:</strong>{" "}
                    {group.patientName}
                  </Typography>
                  <Typography fontSize={isSmallScreen ? "0.92rem" : "1rem"}>
                    <strong>{t("pharmacy.totalPrice", "Total Price")}:</strong>{" "}
                    {group.total} AFN
                  </Typography>
                </Stack>

                {!group.isPaid && (
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={2}
                    sx={{ mt: 1 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        // Collect all unpaid ICU + equipment for this admission
                        const icuIds = group.unpaid
                          .filter((c) => c._source !== "equipment")
                          .map((c) => c.id);
                        // Create charges for equipment assignments on the fly
                        try {
                          const assn = await listPatientEquipmentAssignments(
                            { icu_admission: group.admissionId },
                            token
                          );
                          const assnRows = Array.isArray(assn)
                            ? assn
                            : assn?.results || [];
                          const createdIds = [];
                          for (const a of assnRows) {
                            // eslint-disable-next-line no-await-in-loop
                            const created = await createICUCharge(
                              {
                                admission: group.admissionId,
                                item_name: a.equipment_name,
                                item_type: "Equipment",
                                quantity: 1,
                                unit_price: a.total_charges,
                                description: `Equipment usage: ${
                                  a.equipment_name
                                } (${a.duration_hours || "-"} hrs)`,
                              },
                              token
                            );
                            createdIds.push(created?.id);
                            // eslint-disable-next-line no-await-in-loop
                            await markICUChargePaid(created?.id, token);
                          }
                          // pay existing unpaid icu
                          for (const cid of icuIds) {
                            // eslint-disable-next-line no-await-in-loop
                            await markICUChargePaid(cid, token);
                          }
                          queryClient.invalidateQueries(["icu-all-charges"]);
                          const printIds = [...createdIds, ...icuIds];
                          setSelectedChargeIds(printIds);
                          setPrintMeta({
                            patientName: group.patientName,
                            admissionId: group.admissionId,
                          });
                          setTimeout(
                            () =>
                              handlePrintReceipt({
                                patientName: group.patientName,
                                admissionId: group.admissionId,
                              }),
                            150
                          );
                        } catch (e) {}
                      }}
                    >
                      {t("reception.collectAll", "Collect All")}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={markPaidMutation.isPending}
                      onClick={() => {
                        const ids = group.unpaid.map((c) => c.id);
                        setSelectedChargeIds(ids);
                        markPaidMutation.mutate(ids);
                      }}
                      sx={{
                        minWidth: 160,
                        fontWeight: "bold",
                        borderRadius: 2,
                        py: 1,
                        background: "linear-gradient(90deg, #d32f2f, #f44336)",
                        "&:hover": {
                          background:
                            "linear-gradient(90deg, #f44336, #d32f2f)",
                          boxShadow: 2,
                        },
                      }}
                    >
                      {markPaidMutation.isPending
                        ? t("reception.loading", "Processing...")
                        : t("reception.collectFees", "Collect Fees")}
                    </Button>
                  </Stack>
                )}

                <Box mt={2}>
                  <Typography fontWeight="bold" sx={{ mb: 1 }}>
                    {t("reception.charges", "Charges")}
                  </Typography>
                  {group.charges.map((c) => (
                    <Box
                      key={c.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ py: 0.5, borderBottom: "1px dashed #e0e0e0" }}
                    >
                      <Box>
                        <Typography variant="body2">{c.item_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.charge_date
                            ? new Date(c.charge_date).toLocaleString()
                            : ""}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="body2">
                          {c.quantity} × {c.unit_price} ={" "}
                          <strong>{c.total_amount} AFN</strong>
                        </Typography>
                        <Chip
                          size="small"
                          color={c.is_paid ? "success" : "warning"}
                          label={
                            c.is_paid
                              ? t("reception.paid", "Paid")
                              : t("reception.pending", "Pending")
                          }
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          ))
        )}

        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={4}
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="body2" mr={2}>
                {t("reception.rowsPerPage", "Rows per page")}:
              </Typography>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                style={{ padding: 6, borderRadius: 6 }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </Box>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Box>
        </Grid>
      </Grid>
      {/* Equipment Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeEquipmentDrawer}
        PaperProps={{ sx: { width: { xs: "100%", sm: 560 } } }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">
            {t("reception.equipmentForAdmission", "Equipment for Admission")} #
            {drawerAdmissionId}
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              size="small"
              variant="contained"
              onClick={async () => {
                for (const aId of selectedEqIds) {
                  const a = assignments.find((x) => x.id === aId);
                  if (!a) continue;
                  try {
                    const payload = {
                      admission: drawerAdmissionId,
                      item_name: a.equipment_name,
                      item_type: "Equipment",
                      quantity: 1,
                      unit_price: a.total_charges,
                      description: `Equipment usage: ${a.equipment_name} (${
                        a.duration_hours || "-"
                      } hrs)`,
                    };
                    // eslint-disable-next-line no-await-in-loop
                    const created = await createICUCharge(payload, token);
                    // eslint-disable-next-line no-await-in-loop
                    await markICUChargePaid(created?.id, token);
                  } catch (e) {
                    // continue others
                  }
                }
                queryClient.invalidateQueries(["icu-all-charges"]);
                setSelectedChargeIds([]);
                setSelectedEqIds([]);
                handlePrintReceipt();
              }}
              disabled={selectedEqIds.length === 0}
            >
              {t("reception.collectFees", "Collect Fees")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setPrintMeta({
                  patientName: assignments[0]?.patient_name || "",
                  admissionId: drawerAdmissionId,
                });
                setSelectedChargeIds([]); // will be set during receipt generation if needed
                handlePrintReceipt({
                  patientName: assignments[0]?.patient_name || "",
                  admissionId: drawerAdmissionId,
                });
              }}
              disabled={selectedEqIds.length === 0}
            >
              {t("common.printReceipt", "Print Receipt")}
            </Button>
          </Box>
        </Box>
        <Box sx={{ px: 2, pb: 2 }}>
          {assignmentsQuery.isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} padding="checkbox">
                    <Checkbox
                      onChange={(e) => {
                        setSelectedEqIds(
                          e.target.checked
                            ? assignments
                                .filter((a) => a.status === 1 || !a.end_time)
                                .map((a) => a.id)
                            : []
                        );
                      }}
                      checked={
                        selectedEqIds.length > 0 &&
                        selectedEqIds.length ===
                          assignments.filter(
                            (a) => a.status === 1 || !a.end_time
                          ).length
                      }
                      indeterminate={
                        selectedEqIds.length > 0 &&
                        selectedEqIds.length <
                          assignments.filter(
                            (a) => a.status === 1 || !a.end_time
                          ).length
                      }
                    />
                  </TableCell>
                  <TableCell>{t("reception.equipment", "Equipment")}</TableCell>
                  <TableCell>{t("reception.quantity", "Hours")}</TableCell>
                  <TableCell>{t("common.date", "Date")}</TableCell>
                  <TableCell align="right">
                    {t("common.total", "Total")}
                  </TableCell>
                  <TableCell align="center">
                    {t("common.status", "Status")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedEqIds.includes(a.id)}
                        onChange={() => toggleEq(a.id)}
                      />
                    </TableCell>
                    <TableCell>{a.equipment_name}</TableCell>
                    <TableCell>{a.duration_hours}</TableCell>
                    <TableCell>
                      {(a.start_time &&
                        new Date(a.start_time).toLocaleString()) ||
                        "-"}
                      {a.end_time
                        ? ` → ${new Date(a.end_time).toLocaleString()}`
                        : ""}
                    </TableCell>
                    <TableCell align="right">{a.total_charges} AFN</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        color={a.status === 2 ? "success" : "warning"}
                        label={
                          a.status === 2
                            ? t("reception.completed", "Completed")
                            : t("reception.active", "Active")
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Drawer>
    </Container>
  );
}

